/**
 * Error formatting and clipboard utilities for ErrorDetailsAccordion
 *
 * Provides functions to generate error IDs, format error details as plain text,
 * and copy error information to clipboard for support tickets.
 */

import {AppError} from '../types/errors';

/**
 * Generate deterministic error ID from error properties
 *
 * Format: {category}-{timestamp}-{source}
 * Source is truncated to first 20 characters for readable IDs
 */
export function generateErrorId(error: AppError): string {
    const timestamp =
        error.timestamp instanceof Date
            ? error.timestamp.getTime()
            : new Date(error.timestamp as any).getTime();
    const source = error.source.substring(0, 20);
    return `${error.category}-${timestamp}-${source}`;
}

/**
 * Format error details as plain text for display and copying
 *
 * Includes error ID, category, source, timestamp, retry count, HTTP status,
 * original error message, stack trace (first 10 lines), and footer text.
 */
export function formatErrorDetails(error: AppError): string {
    const errorId = generateErrorId(error);
    const timestamp =
        error.timestamp instanceof Date
            ? error.timestamp.toISOString()
            : error.timestamp;

    // Extract HTTP status from originalError if available
    const httpStatus =
        error.originalError?.status ||
        error.originalError?.response?.status ||
        'N/A';

    // Extract stack trace (first 10 lines)
    const stack = error.originalError?.stack || '';
    const stackLines = stack.split('\n').slice(0, 10).join('\n');
    const stackDisplay = stackLines || 'No stack trace available';

    return `Error Details
─────────────────
Error ID: ${errorId}
Category: ${error.category}
Source: ${error.source}
Timestamp: ${timestamp}
Retry Count: ${error.retryCount}
HTTP Status: ${httpStatus}

Original Error:
${error.originalError?.message || 'No message available'}

Stack Trace:
${stackDisplay}

─────────────────
Copy this information when contacting support.`;
}

/**
 * Copy error details to clipboard
 *
 * Uses Clipboard API (baseline since March 2020) with fallback for older browsers.
 * Returns true if successful, false otherwise.
 */
export async function copyErrorDetails(error: AppError): Promise<boolean> {
    const text = formatErrorDetails(error);

    // Try Clipboard API (baseline since March 2020)
    if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (err) {
            console.error('Clipboard API failed:', err);
            // Fall through to fallback
        }
    }

    // Fallback for browsers without Clipboard API (very rare in 2026)
    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        return success;
    } catch (err) {
        console.error('Fallback copy failed:', err);
        return false;
    }
}
